import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { RestService } from '../rest/rest.service';
import { BackendError } from '../../domain/error.domain';
import { TimezoneService } from '../date/timezone.service';

import {
  Rating,
  TopMoversSortType,
  TopMover,
  TopMovers,
  RatingConsensus,
  EventsData,
  BaseMarketEvent,
  MarketEvent,
  NewsReport,
  EventType,
  SimpleSecurity,
  TopMoversWatchlist,
  WatchlistItemResponse
} from '../../domain/public-api';
import { MarketInfoNewsRestService } from '../market-info/market-info-news-rest-service';
import { DateService } from '../date/date.service';

@Injectable({
  providedIn: 'root'
})
export class TopMoversRestService {

  private static readonly FIELDS_BY_EVENT_TYPE: unknown = {
    EARNINGS: 'date',
    DIVIDENDS: 'exDate',
    RATING_CHANGES: 'eventDate',
    NEWS: 'reportDatetime'
  };

  private readonly MAX_TOP_MOVERS: number = 5;

  constructor(private readonly restService: RestService,
              private readonly timezoneService: TimezoneService,
              private readonly marketInfoNewsRestService: MarketInfoNewsRestService) {
  }

  getTopMovers(sortType?: TopMoversSortType): Observable<TopMovers> {

    const params = sortType === undefined ? undefined :
      {
        orderByMarketValue: sortType.orderByMarketValue
      };
    return this.restService.getResource('portfolio.topMovers')
      .get<TopMovers>({ params })
      .pipe(
        map((response: TopMovers): TopMovers => {
          const topMovers = response.topMovers !== undefined && response.topMovers.length > this.MAX_TOP_MOVERS ?
            response.topMovers.slice(0, this.MAX_TOP_MOVERS) :
            response.topMovers;
          return {
            topMovers,
            asOfDate: response.asOfDate
          };
        }),
        catchError((errorResponse: Array<BackendError>): Observable<never> => {
          const isAgreementDeclinedError = this.isAgreementDeclinedError(errorResponse);
          return throwError(() =>isAgreementDeclinedError ? 'AGREEMENT_DECLINED' : errorResponse);
        })
      );
  }

  getTopMoversWatchlist(): Observable<TopMoversWatchlist> {
    return this.restService.getResource('topMoversAndWatchlist.topMoversWatchlist')
      .get<TopMoversWatchlist>()
      .pipe(
        map((response: TopMoversWatchlist): TopMoversWatchlist => response)
      );
  }

  getTopMoversWatchlistAndNews(): Observable<TopMoversWatchlist> {
    return this.getTopMoversWatchlist()
      .pipe(
        mergeMap((response: TopMoversWatchlist): Observable<TopMoversWatchlist> =>
          this.addNewsForTopMoversWatchlist(response)
            .pipe(
              map((): TopMoversWatchlist => response),
              catchError((error: BackendError): Observable<never> => {
                throw error;
              }))
        ));
  }

  addRatings(topMovers: Array<TopMover>): Observable<void> {
    const securities = this.getArrayOfTopMoversSecuritySymbols(topMovers);
    if (securities.length > 0) {
      return this.getRatings(securities).pipe(map((ratings: Array<Rating>): void => this.attachRatings(topMovers, ratings)));
    }
    return of();
  }

  addEventsAndNewsToTopMovers(topMovers: Array<TopMover>, marketEvents: EventsData): Observable<void> {
    let securitiesWithoutEvents;
    const topMoversNoEvents = this.addEventsToTopMovers(topMovers, marketEvents);

    if (topMoversNoEvents.length > 0) {
      securitiesWithoutEvents = this.getArrayOfTopMoversSecuritySymbols(topMoversNoEvents);
      return this.getNews(securitiesWithoutEvents, 1)
        .pipe(
          map((newsOver90Days: Array<NewsReport>): void => {
            this.addNewsToTopMovers(topMovers, newsOver90Days);
          })
        );
    }
    return of();
  }

  private getRatings(securities: Array<string>): Observable<Array<Rating>> {
    return this.restService.getResource('security.ratings').get({
      params: { securities }
    });
  }

  private addEventsToTopMovers(topMovers: Array<TopMover>, events: EventsData): Array<TopMover> {
    const topMoversWithoutEvents = [];
    topMovers.forEach((topMover: TopMover): void => {
      topMover.eventOrNews = this.findEventForSecurity(topMover.holding.security, this.findTodayEvents(events));
      if (topMover.eventOrNews === undefined) {
        topMoversWithoutEvents.push(topMover);
      }
    });
    return topMoversWithoutEvents;
  }

  private isAgreementDeclinedError(errorResponses: Array<BackendError>): boolean {
    if ( errorResponses !== undefined) {
      return errorResponses
        .find((backendError: BackendError): boolean => backendError.code === 'QUOTE.ERROR.AGREEMENT_DECLINED') !== undefined;
    }
    return false;
  }

  private addNewsForTopMoversWatchlist(topMoversWatchlists: TopMoversWatchlist): Observable<unknown> {
    let securities;
    if (topMoversWatchlists.topMovers.length > 0 || topMoversWatchlists.watchList.length > 0) {
      securities = this.getArrayOfTopMoversWatchlistSecuritySymbols(topMoversWatchlists);
      return this.getNews(securities, 1)
        .pipe(
          map((newsOver90Days: Array<NewsReport>): void => {
            this.addNewsToTopMoversWatchlists(topMoversWatchlists, newsOver90Days);
          })
        );
    }
    return of(topMoversWatchlists);
  }

  private addNewsToTopMoversWatchlists(topMoversWatchlists: TopMoversWatchlist, news: Array<NewsReport>): void {
    this.addNewsToTopMovers(topMoversWatchlists.topMovers, news);
    this.addNewsToWatchlists(topMoversWatchlists.watchList, news);
  }

  private addNewsToTopMovers(topMoversWithNoNews: Array<TopMover>, news: Array<NewsReport>): void {
    topMoversWithNoNews.forEach((topMover: TopMover): void => {
      const eventsArray = this.findEventOrNewsBySecurity(topMover.holding.security, news);
      if (eventsArray.length > 0) {
        topMover.eventOrNews = eventsArray[0];
        topMover.eventOrNews.securityEventType = EventType.NEWS;
      }
    }
    );
  }

  private addNewsToWatchlists(watchlistsWithNoNews: Array<WatchlistItemResponse>, news: Array<NewsReport>): void {
    watchlistsWithNoNews.forEach((watchlist: WatchlistItemResponse): void => {
      const watchlistSecurity: SimpleSecurity  = {
        symbol: watchlist.tickerSymbol,
        market: watchlist.market
      };
      const watchlistNews = this.findEventOrNewsBySecurity(watchlistSecurity, news);
      if (watchlistNews.length > 0) {
        watchlist.news = watchlistNews[0];
        watchlist.news.securityEventType = EventType.NEWS;
      }
    });
  }

  private findTodayEvents(eventResponse: EventsData): Array<MarketEvent> {
    const todayEvents = [];
    const presentDay = this.timezoneService.getCurrentDateInServerTimezone();
    Object.keys(eventResponse.events).forEach((eventGroup: string): void => {
      eventResponse.events[eventGroup].forEach((event: MarketEvent): void => {
        const dateField = TopMoversRestService.FIELDS_BY_EVENT_TYPE[event.securityEventType];
        if (DateService.isSameDay(event[dateField], presentDay)) {
          todayEvents.push(event);
        }
      });
    });

    return todayEvents;
  }

  private findEventOrNewsBySecurity<T extends { security: SimpleSecurity }>(security: SimpleSecurity, events: Array<T>): Array<T> {
    return events.filter((eventItem: T): boolean => this.compareSecurities(eventItem.security, security));
  }

  private findEventForSecurity(security: SimpleSecurity, events: Array<MarketEvent>): MarketEvent {
    const eventsForThisSecurity = this.findEventOrNewsBySecurity(security, events);
    let foundEvent;
    if (eventsForThisSecurity.length > 0) {
      // Each security can have multiple news items, we select an item based on the following priority
      foundEvent = this.findEventByType(EventType.EARNINGS, eventsForThisSecurity);
      // if there is no earnings event check for dividends event
      if (foundEvent === undefined) {
        foundEvent = this.findEventByType(EventType.DIVIDENDS, eventsForThisSecurity);
      }
      // if there is no dividends event check for rating_change event
      // we only match on Rating Changes for the present day
      if (foundEvent === undefined) {
        foundEvent = this.findEventByType(EventType.RATING_CHANGES, eventsForThisSecurity);
      }
    }
    return foundEvent;
  }

  private findEventByType(eventType: EventType, events: Array<BaseMarketEvent>): BaseMarketEvent {
    for (const event of events) {
      if (event.securityEventType === eventType) {
        return event;
      }
    }
  }

  // securities array of <ticker>-<market> values
  // TODO: should be moved to markets rest service
  private getNews(securities: Array<string>, limit: number,
                  dateRange?: { startDate: Date; toDate: Date }): Observable<Array<NewsReport>> {
    return this.marketInfoNewsRestService.getSecuritiesNews(securities, limit, dateRange);
  }

  private getArrayOfTopMoversWatchlistSecuritySymbols(topMoversWatchlists: TopMoversWatchlist): Array<string> {
    const topMoversSymbols = this.getArrayOfTopMoversSecuritySymbols(topMoversWatchlists.topMovers);
    const watchlistsSymbols = this.getArrayOfWatchlistSecuritySymbols(topMoversWatchlists.watchList);
    return [...new Set([...topMoversSymbols, ...watchlistsSymbols])];
  }

  private getArrayOfWatchlistSecuritySymbols(watchlists: Array<WatchlistItemResponse>): Array<string> {
    return watchlists.map((watchlist: WatchlistItemResponse): string => watchlist.tickerSymbol + '-' + watchlist.market);
  }

  private getArrayOfTopMoversSecuritySymbols(topMovers: Array<TopMover>): Array<string> {
    return topMovers.map((topMover: TopMover): string => topMover.holding.security.symbol + '-' + topMover.holding.security.market);
  }

  private attachRatings(topMovers: Array<TopMover>, ratings: Array<Rating>): void {
    topMovers.forEach((topMover: TopMover): void => {
      topMover.ratingConsensus = this.findRatingBySecurity(topMover.holding.security, ratings);
    });
  }

  private findRatingBySecurity(security: SimpleSecurity, ratings: Array<Rating>): RatingConsensus {
    const foundRating =  ratings.find((rating: Rating): boolean => this.compareSecurities(rating.security, security));

    if (foundRating !== undefined) {
      return foundRating.ratingConsensus;
    }
  }

  private compareSecurities(securityOne: SimpleSecurity, securityTwo: SimpleSecurity): boolean {
    return securityOne !== undefined &&
      securityTwo !== undefined &&
      (securityOne.symbol === securityTwo.symbol && securityOne.market === securityTwo.market);
  }
}

